import { differenceInCalendarDays, format, isSameMonth, isSameYear, parseISO, startOfToday } from 'date-fns'
import type { BadgeVariant } from '@/components/ui'
import type { MeData, MeRoleId } from '@/types/api.types'
import type { LeaveDocument, LeaveStatus } from '@/features/leave/types/leave.types'

export function leaveStatusBadgeVariant(status: LeaveStatus): BadgeVariant {
  switch (status) {
    case 'pending':
      return 'warning'
    case 'pending_admin':
      return 'info'
    case 'approved':
      return 'success'
    case 'rejected':
      return 'danger'
    case 'cancelled':
      return 'neutral'
    default:
      return 'neutral'
  }
}

export function leaveStatusLabel(status: LeaveStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending'
    case 'pending_admin':
      return 'Admin review'
    case 'approved':
      return 'Approved'
    case 'rejected':
      return 'Rejected'
    case 'cancelled':
      return 'Cancelled'
    default:
      return status
  }
}

const LEAVE_TYPE_LABEL: Record<string, string> = {
  annual: 'Annual',
  sick: 'Sick',
  casual: 'Casual',
  unpaid: 'Unpaid',
  maternity: 'Maternity',
  paternity: 'Paternity',
  other: 'Other',
}

export function leaveTypeLabel(type: string): string {
  return (
    LEAVE_TYPE_LABEL[type] ??
    type.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  )
}

export function resolveRequesterName(
  requesterName: string | undefined,
  requesterEmployeeId: string | undefined,
  userId: string,
  requesterId?: string,
): string {
  if (requesterName && requesterEmployeeId) return `${requesterName} (${requesterEmployeeId})`
  return requesterName ?? requesterEmployeeId ?? requesterId ?? userId
}

/** Extracts the role slug from the populated MeData.roleId (or returns null). */
export function getRoleSlug(me: MeData | undefined): string | null {
  if (!me?.roleId || typeof me.roleId === 'string') return null
  return (me.roleId as MeRoleId).slug ?? null
}

/**
 * Whether the viewer can approve or reject this request right now. A request in
 * admin review only moves for an admin — the backend refuses anyone else.
 * Approve and reject are separate permissions; either one makes a decider.
 */
export function canDecideLeave(
  status: LeaveStatus,
  canApproveOrReject: boolean,
  roleSlug: string | null,
): boolean {
  if (!canApproveOrReject) return false
  if (status === 'pending') return true
  return status === 'pending_admin' && roleSlug === 'admin'
}

/**
 * Leave dates are calendar days stored as midnight UTC. Reading only the date
 * part keeps "5 Oct" on 5 Oct in every timezone.
 */
export function parseLeaveDate(value: string): Date {
  return parseISO(value.slice(0, 10))
}

/** Inclusive calendar-day count, or 0.5 for a half day. */
export function leaveDayCount(leave: Pick<LeaveDocument, 'startDate' | 'endDate' | 'durationType'>): number {
  if (leave.durationType === 'half') return 0.5
  const days =
    differenceInCalendarDays(parseLeaveDate(leave.endDate), parseLeaveDate(leave.startDate)) + 1
  return Math.max(1, days)
}

/** "Half day", "1 day", "3 days". */
export function formatLeaveLength(leave: Pick<LeaveDocument, 'startDate' | 'endDate' | 'durationType'>): string {
  const days = leaveDayCount(leave)
  if (days === 0.5) return 'Half day'
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

/**
 * Compact range that drops whatever the two ends share:
 * "Mon, 5 Oct 2026", "5 – 7 Oct 2026", "28 Sep – 3 Oct 2026", "29 Dec 2026 – 2 Jan 2027".
 */
export function formatLeaveRange(startDate: string, endDate: string): string {
  const start = parseLeaveDate(startDate)
  const end = parseLeaveDate(endDate)
  if (differenceInCalendarDays(end, start) <= 0) return format(start, 'EEE, d MMM yyyy')
  if (!isSameYear(start, end)) return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`
  if (!isSameMonth(start, end)) return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`
  return `${format(start, 'd')} – ${format(end, 'd MMM yyyy')}`
}

export type LeaveTiming = { label: string; tone: 'now' | 'soon' | 'later' | 'past' }

/**
 * Where the leave sits relative to today. Only meaningful for requests that are
 * still live (pending or approved); a rejected or cancelled leave has no timing.
 */
export function leaveTiming(
  leave: Pick<LeaveDocument, 'startDate' | 'endDate' | 'status'>,
  today: Date = startOfToday(),
): LeaveTiming | null {
  if (leave.status === 'rejected' || leave.status === 'cancelled') return null
  const start = parseLeaveDate(leave.startDate)
  const end = parseLeaveDate(leave.endDate)
  const untilStart = differenceInCalendarDays(start, today)
  const sinceEnd = differenceInCalendarDays(today, end)

  if (untilStart <= 0 && sinceEnd <= 0) {
    // A pending request whose dates have arrived is overdue for a decision,
    // not "on leave" — nobody has said yes yet.
    if (leave.status !== 'approved') return { label: 'Started', tone: 'now' }
    return { label: 'On leave now', tone: 'now' }
  }
  if (untilStart === 1) return { label: 'Starts tomorrow', tone: 'soon' }
  if (untilStart > 1 && untilStart <= 7) return { label: `Starts in ${untilStart} days`, tone: 'soon' }
  if (untilStart > 7) return { label: `Starts ${format(start, 'd MMM')}`, tone: 'later' }
  if (sinceEnd === 1) return { label: 'Ended yesterday', tone: 'past' }
  return { label: `Ended ${sinceEnd} days ago`, tone: 'past' }
}
